/* eslint-disable react/prop-types */
import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Box from "@material-ui/core/Box";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";

import { CREATE_COURSE, UPDATE_COURSE } from "graphql/mutation/admin";
import { useStyles } from "Components/Modal/ModalStyles";
import { ReactComponent as CloseIcon } from "Assets/Svg/close.svg";

// Exact backend enum values (CourseType / CourseTag in course schema)
const COURSE_TYPES = [
  "Photography",
  "Cinemetography",
  "Drone_And_Aerial_Cinemetography",
  "Video_Editing",
  "Photo_Editing_And_Design",
  "Studio_And_Lighting",
  "Business_And_Marketing",
  "AI_Course",
];
const COURSE_TAGS = ["Beginner", "Intermediate", "Advance"];

const humanize = (value) => value.replace(/_And_/g, " & ").replace(/_/g, " ");

const validationSchema = yup.object({
  name: yup
    .string()
    .trim()
    .max(150, "Name must be at most 150 characters")
    .required("Name is required"),
  description: yup
    .string()
    .max(1000, "Description must be at most 1000 characters"),
  type: yup
    .string()
    .oneOf([...COURSE_TYPES, ""], "Select a valid type"),
  tag: yup.string().oneOf([...COURSE_TAGS, ""], "Select a valid tag"),
  price: yup
    .number()
    .transform((val, orig) => (orig === "" ? undefined : val))
    .typeError("Price must be a number")
    .min(0, "Price cannot be negative"),
  owner: yup.string().max(150, "Owner must be at most 150 characters"),
});

const CourseFormModal = ({ open, handleClose, handleOnSuccess, course }) => {
  const classes = useStyles();
  const isEdit = Boolean(course?.id);
  const [loading, setLoading] = useState(false);
  const [createCourse] = useMutation(CREATE_COURSE);
  const [updateCourse] = useMutation(UPDATE_COURSE);

  const toPrice = (value) =>
    value === "" || value === null || value === undefined
      ? undefined
      : Number(value);

  const handleSubmitForm = async (values) => {
    try {
      setLoading(true);
      let status;
      let message;

      if (isEdit) {
        // updateCourse does NOT accept isActive — omit it.
        const { data } = await updateCourse({
          variables: {
            input: {
              id: course.id,
              name: values.name.trim(),
              description: values.description?.trim() ?? "",
              type: values.type || null,
              tag: values.tag || null,
              price: toPrice(values.price) ?? null,
              owner: values.owner?.trim() || null,
            },
          },
        });
        ({ status, message } = data.updateCourse);
      } else {
        const { data } = await createCourse({
          variables: {
            input: {
              name: values.name.trim(),
              description: values.description?.trim() || undefined,
              type: values.type,
              tag: values.tag || undefined,
              price: toPrice(values.price),
              isActive: values.isActive,
              owner: values.owner?.trim() || undefined,
            },
          },
        });
        ({ status, message } = data.createCourse);
      }

      if (status === "success") {
        toast.success(
          message || `Course ${isEdit ? "updated" : "created"} successfully`
        );
        handleOnSuccess();
        handleClose();
      } else {
        toast.error(message || "Something went wrong !");
      }
      setLoading(false);
    } catch (err) {
      toast.error(err.message || "Something went wrong !");
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      name: course?.name ?? "",
      description: course?.description ?? "",
      type: course?.type ?? "",
      tag: course?.tag ?? "",
      price: course?.price ?? "",
      owner: course?.owner ?? "",
      isActive: course?.isActive ?? true,
    },
    enableReinitialize: true,
    validationSchema,
    validateOnMount: true,
    onSubmit: (values) => handleSubmitForm(values),
  });

  const canSubmit =
    !loading && formik.isValid && (!isEdit || formik.dirty);

  return (
    <Dialog
      open={open}
      aria-labelledby="course-form-title"
      maxWidth="sm"
      onClose={() => {
        if (!loading) handleClose();
      }}
      PaperProps={{
        style: {
          borderRadius: 15,
          width: 500,
          maxWidth: "95vw",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogContent
        classes={{ root: classes.dialogContext }}
        style={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        {/* Fixed header */}
        <div
          role="button"
          tabIndex={0}
          onKeyDown={loading ? undefined : handleClose}
          onClick={loading ? undefined : handleClose}
          className={classes.topBar}
          style={{ flexShrink: 0 }}
        >
          <span className={classes.title}>
            {isEdit ? "Edit Course" : "Create Course"}
          </span>
          <span className={`${classes.onHover} ${classes.closeBtn}`}>
            <CloseIcon />
          </span>
        </div>

        <form
          noValidate
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Scrollable fields */}
          <Box style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
            <Grid container spacing={2} style={{ marginTop: "2vh", padding: "0px 30px" }}>
              <Grid item xs={12}>
                <TextField
                  id="name"
                  name="name"
                  label="Name"
                  placeholder="Course name"
                  variant="outlined"
                  fullWidth
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="type"
                  name="type"
                  label="Type (optional)"
                  select
                  variant="outlined"
                  fullWidth
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.type && Boolean(formik.errors.type)}
                  helperText={formik.touched.type && formik.errors.type}
                >
                  <MenuItem value="">None</MenuItem>
                  {COURSE_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {humanize(t)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="tag"
                  name="tag"
                  label="Tag"
                  select
                  variant="outlined"
                  fullWidth
                  value={formik.values.tag}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.tag && Boolean(formik.errors.tag)}
                  helperText={formik.touched.tag && formik.errors.tag}
                >
                  <MenuItem value="">None</MenuItem>
                  {COURSE_TAGS.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="price"
                  name="price"
                  type="number"
                  label="Price (optional)"
                  placeholder="0"
                  variant="outlined"
                  fullWidth
                  value={formik.values.price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.price && Boolean(formik.errors.price)}
                  helperText={formik.touched.price && formik.errors.price}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="owner"
                  name="owner"
                  label="Owner (optional)"
                  variant="outlined"
                  fullWidth
                  value={formik.values.owner}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.owner && Boolean(formik.errors.owner)}
                  helperText={formik.touched.owner && formik.errors.owner}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="description"
                  name="description"
                  label="Description"
                  placeholder="Course description"
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={3}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.description &&
                    Boolean(formik.errors.description)
                  }
                  helperText={
                    formik.touched.description && formik.errors.description
                  }
                />
              </Grid>
              {!isEdit && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formik.values.isActive}
                        onChange={(e) =>
                          formik.setFieldValue("isActive", e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Fixed footer */}
          <Box
            style={{
              flexShrink: 0,
              borderTop: "1px solid #00000014",
              display: "flex",
              justifyContent: "space-evenly",
              padding: "16px 30px",
            }}
          >
            <Button
              variant="contained"
              size="small"
              style={{
                width: "155px",
                height: "40px",
                fontWeight: "550",
                color: "black",
                boxShadow: "none",
                fontSize: "14px",
              }}
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              type="submit"
              style={{
                width: "155px",
                height: "40px",
                fontWeight: "550",
                boxShadow: "none",
                fontSize: "14px",
                background: `var(--color-primary)`,
                color: "white",
                opacity: canSubmit ? 1 : 0.6,
              }}
              disabled={!canSubmit}
            >
              {loading && (
                <CircularProgress
                  style={{
                    width: "20px",
                    height: "20px",
                    color: "#fff",
                    marginRight: "10px",
                  }}
                />
              )}
              {/* eslint-disable-next-line no-nested-ternary */}
              {loading
                ? isEdit
                  ? "Saving..."
                  : "Creating..."
                : isEdit
                ? "Save"
                : "Create"}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CourseFormModal;
